import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";

const PatientProfile = () => {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')
        if (!token) {
          setError("User token not found. Please log in again.");
          setLoading(false);
          return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${API_BASE_URL}/PatientPortal/PatientPortalById`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch patient profile.");
        }

        const result = await response.json();
        setPatient(result);
      } catch (err) {
        setError(err.message || "Failed to load patient data.");
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, []);

  // --- STYLES ---

  // 1. Breadcrumb Style (Forces White Text in Dark Mode)
  const breadcrumbSx = {
    ".dark-theme &": {
      "& h5": {
        color: "#fff !important",
      },
      "& .breadcrumb": {
        "& li": {
          color: "#fff !important",
        },
        "& li a": {
          color: "#fff !important",
        },
        "& i": {
          color: "#fff !important",
        }
      }
    }
  };

  // 2. Paper / Card Style (Dark Mode Background & Text)
  const paperSx = {
    p: 4,
    borderRadius: 3,
    maxWidth: "1100px",
    mx: "auto",
    boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
    bgcolor: "#fff",
    
    // Dark Mode Overrides
    ".dark-theme &": {
      bgcolor: "#0e1726", // Dark Card Background
      color: "#fff",      // Default Text White
      
      // Override specific text elements inside
      "& .MuiTypography-root": {
        color: "#fff !important",
      },
      "& h5, & h6": {
        color: "#fff !important",
      },
      "& strong": {
        color: "#fff !important",
      },
      "& .MuiChip-root": {
        color: "#fff !important",
        borderColor: "rgba(255,255,255,0.2)"
      },
      // Subtle color for secondary text/captions
      "& .MuiTypography-caption": {
        color: "rgba(255,255,255,0.7) !important"
      },
      "& .MuiTypography-body2": {
         color: "rgba(255,255,255,0.7) !important"
      }
    }
  };

  if (loading)
    return (
      <Box
        sx={{
          height: "calc(100vh - 150px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );

  if (error)
    return (
      <Box
        sx={{
          height: "calc(100vh - 150px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );

  if (!patient)
    return (
      <Box
        sx={{
          height: "calc(100vh - 150px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>No patient data available.</Typography>
      </Box>
    );

  const fullName = `${patient.firstName ?? ""} ${patient.middlename ?? ""} ${patient.lastName ?? ""
    }`.trim();
  const genderLabel =
    patient.gender === "2"
      ? "Female"
      : patient.gender === "1"
        ? "Male"
        : "Unknown";
  const statusText = patient.inactive ? "Inactive" : "Active";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 80px)",
        // bgcolor: "#f9fafb", // Keeping your commented out logic
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 6 },
      }}
    >
      {/* Breadcrumb */}
      {/* Replaced div with Box to apply sx for dark mode */}
      <Box className="breadcrumb-card" sx={{ marginBottom: "25px", ...breadcrumbSx }}>
        <h5>Patient Profile</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/forms">
              <i className="material-symbols-outlined">home</i> Dashboard
            </Link>
          </li>
          <li>Patient</li>
          <li>Profile</li>
        </ul>
      </Box>

      {/* Unified Card */}
      <Paper sx={paperSx}>
        {/* Header */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Avatar
            src="/images/patient-avatar.jpg"
            alt={fullName}
            sx={{ width: 110, height: 110, mx: "auto", mb: 2 }}
          />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {fullName || "Unnamed Patient"}
          </Typography>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
            Patient ID – {patient.id}
          </Typography>
          <Chip
            label={statusText}
            color={statusText === "Active" ? "success" : "default"}
            size="small"
            variant="outlined" // Using outlined for better contrast in dark mode usually
          />
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'rgba(0,0,0,0.12)', ".dark-theme &": { borderColor: 'rgba(255,255,255,0.12)' } }} />

        {/* Personal Info */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
        >
          Personal Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>First Name:</strong> {patient.firstName || "–"}
            </Typography>
            <Typography>
              <strong>Middle Name:</strong> {patient.middlename || "–"}
            </Typography>
            <Typography>
              <strong>Last Name:</strong> {patient.lastName || "–"}
            </Typography>
            <Typography>
              <strong>Gender:</strong> {genderLabel}
            </Typography>
            <Typography>
              <strong>Date of Birth:</strong>{" "}
              {patient.dOB ? patient.dOB.split("T")[0] : "–"}
            </Typography>
            <Typography>
              <strong>Phone Number:</strong> {patient.phoneNumber || "–"}
            </Typography>
            <Typography>
              <strong>Email:</strong> {patient.emailAddress || "–"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Address:</strong> {patient.address || "–"}
            </Typography>
            <Typography>
              <strong>City:</strong> {patient.city || "–"}
            </Typography>
            <Typography>
              <strong>State:</strong> {patient.state || "–"}
            </Typography>
            <Typography>
              <strong>ZIP Code:</strong> {patient.zipCode || "–"}
            </Typography>
            <Typography>
              <strong>Race:</strong> {patient.race || "–"}
            </Typography>
            <Typography>
              <strong>Ethnicity:</strong> {patient.ethincity || "–"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3, borderColor: 'rgba(0,0,0,0.12)', ".dark-theme &": { borderColor: 'rgba(255,255,255,0.12)' } }} />

        {/* Account Details */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
        >
          Account & Practice
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Account No:</strong> {patient.accountNo || "–"}
            </Typography>
            <Typography>
              <strong>Practice ID:</strong> {patient.practiceId || "–"}
            </Typography>
            <Typography>
              <strong>Provider ID:</strong> {patient.providerId || "–"}
            </Typography>
            <Typography>
              <strong>Billing Rendering Provider:</strong>{" "}
              {patient.billingRenderingProvider || "–"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Primary Staff:</strong> {patient.primaryStaff || "–"}
            </Typography>
            <Typography>
              <strong>Type of Patient:</strong> {patient.typeOfPatient || "–"}
            </Typography>
            <Typography>
              <strong>Illness Date:</strong>{" "}
              {patient.illnessDate
                ? patient.illnessDate.split("T")[0]
                : "–"}
            </Typography>
            <Typography>
              <strong>Intake Date:</strong>{" "}
              {patient.intakeDate ? patient.intakeDate.split("T")[0] : "–"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3, borderColor: 'rgba(0,0,0,0.12)', ".dark-theme &": { borderColor: 'rgba(255,255,255,0.12)' } }} />

        {/* Diagnosis */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
        >
          Diagnosis Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Diagnosis 1:</strong> {patient.diagnosis1 || "–"}
            </Typography>
            <Typography>
              <strong>Diagnosis 2:</strong> {patient.diagnosis2 || "–"}
            </Typography>
            <Typography>
              <strong>Diagnosis :</strong> {patient.diagnosis || "–"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Assigned To:</strong> {patient.assignedto || "–"}
            </Typography>
            <Typography>
              <strong>Signature On File:</strong>{" "}
              {patient.signature === "true" ? "Yes" : "No"}
            </Typography>
            <Typography>
              <strong>Medicare Status:</strong> {patient.medicareStatus || "–"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3, borderColor: 'rgba(0,0,0,0.12)', ".dark-theme &": { borderColor: 'rgba(255,255,255,0.12)' } }} />

        {/* System Audit */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
        >
          System Audit
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Created By:</strong> {patient.createdBy || "–"}
            </Typography>
            <Typography>
              <strong>Created Date:</strong>{" "}
              {patient.createdDate
                ? new Date(patient.createdDate).toLocaleDateString()
                : "–"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Updated By:</strong> {patient.updatedBy || "–"}
            </Typography>
            <Typography>
              <strong>Updated Date:</strong>{" "}
              {patient.updatedDate
                ? new Date(patient.updatedDate).toLocaleDateString()
                : "–"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3, mb: 1, borderColor: 'rgba(0,0,0,0.12)', ".dark-theme &": { borderColor: 'rgba(255,255,255,0.12)' } }} />
        <Typography
          variant="caption"
          display="block"
          align="center"
          sx={{ color: "#777", ".dark-theme &": { color: "rgba(255,255,255,0.5) !important" } }}
        >
          Profile is viewable‑only – editing disabled.
        </Typography>
      </Paper>
    </Box>
  );
};

export default PatientProfile;